import { Truck, Phone, AlertTriangle, Scale, FileCheck, Building } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const TruckAccident = () => {
  return (
    <GenericPracticePage
      title="Truck Accident Attorney"
      subtitle="Serious truck accident cases require experienced legal representation. We take on trucking companies and their insurers to get you maximum compensation for catastrophic injuries."
      icon={<Truck className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Houston Truck Accident Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              18-wheeler and commercial truck accidents are far more serious than typical car accidents. The size and weight of these vehicles can cause catastrophic injuries and fatalities. These cases are complex, involving federal regulations, multiple liable parties, and aggressive corporate legal teams.
            </p>
            <p className="text-lg text-muted-foreground">
              You need an attorney with specific experience handling truck accident litigation who understands trucking industry regulations and isn't intimidated by large corporations.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Why Truck Accidents Are Different</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <AlertTriangle className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Catastrophic Injuries</h3>
                <p className="text-sm text-muted-foreground">The force of impact from an 80,000-pound truck often causes severe, life-changing injuries or death</p>
              </div>
              <div className="p-4 border rounded-lg">
                <Building className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Multiple Liable Parties</h3>
                <p className="text-sm text-muted-foreground">Driver, trucking company, cargo loaders, maintenance companies, and manufacturers may all share liability</p>
              </div>
              <div className="p-4 border rounded-lg">
                <FileCheck className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Federal Regulations</h3>
                <p className="text-sm text-muted-foreground">FMCSA rules govern hours of service, maintenance, driver qualifications, and cargo securement</p>
              </div>
              <div className="p-4 border rounded-lg">
                <Scale className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Higher Insurance Policies</h3>
                <p className="text-sm text-muted-foreground">Commercial trucks carry million-dollar policies, but insurers fight hard to minimize payouts</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common Causes of Truck Accidents</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg space-y-3">
              <p className="text-muted-foreground">• <strong>Driver Fatigue:</strong> Hours of service violations pushing drivers beyond safe limits</p>
              <p className="text-muted-foreground">• <strong>Speeding & Reckless Driving:</strong> Aggressive driving to meet tight delivery schedules</p>
              <p className="text-muted-foreground">• <strong>Improper Loading:</strong> Overweight or unsecured cargo causing loss of control</p>
              <p className="text-muted-foreground">• <strong>Poor Maintenance:</strong> Brake failures, tire blowouts, and mechanical defects</p>
              <p className="text-muted-foreground">• <strong>Inadequate Training:</strong> Unqualified drivers operating massive commercial vehicles</p>
              <p className="text-muted-foreground">• <strong>Distracted Driving:</strong> Phone use, eating, or other distractions behind the wheel</p>
              <p className="text-muted-foreground">• <strong>DUI/Substance Abuse:</strong> Driving under the influence of drugs or alcohol</p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Our Truck Accident Investigation</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Immediate Evidence Preservation</h3>
                  <p className="text-sm text-muted-foreground">We act fast to secure black box data, driver logs, and maintenance records before they disappear</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Expert Analysis</h3>
                  <p className="text-sm text-muted-foreground">Accident reconstructionists, trucking industry experts, and medical specialists build your case</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Regulatory Violations</h3>
                  <p className="text-sm text-muted-foreground">We identify violations of federal trucking regulations that contributed to your accident</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">All Liable Parties</h3>
                  <p className="text-sm text-muted-foreground">We pursue every responsible party to maximize your compensation</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Time Is Critical</h2>
            <p className="text-muted-foreground mb-4">
              Trucking companies have teams that respond to accidents immediately to protect their interests. Evidence disappears quickly:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Black box data may be overwritten after 30 days</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Driver logs and maintenance records can be "lost"</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Accident scenes are quickly cleared and evidence removed</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Witnesses become harder to locate as time passes</span>
              </li>
            </ul>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Injured by a Commercial Truck?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us immediately for a consultation. We handle all aspects of your case while you focus on recovery. No fee unless we win.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default TruckAccident;
